import { createClient } from "@supabase/supabase-js";
import { getCustomMap, isValidSlug } from "../levels/customMaps.js";

// --- Configuration ---
// Local dev points at the PRODUCTION Supabase project (set VITE_SUPABASE_URL /
// VITE_SUPABASE_ANON_KEY in .env.local) but reads/writes the shared dev_ tables
// via VITE_TABLE_PREFIX. See docs/leaderboard-backend.md.
const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL || "http://127.0.0.1:54321"; // Fallback only
const SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY || "PASTE_YOUR_LOCAL_ANON_KEY_HERE"; // Fallback only

// Table prefix selects which physical table set to use within the prod project:
// "dev_" for the shared dev tables (dev_runs), "" for the real prod tables.
// Set VITE_TABLE_PREFIX=dev_ in .env.local; unset in production builds.
const TABLE_PREFIX = import.meta.env.VITE_TABLE_PREFIX || "";

// Safety net for the dev→prod footgun: in a dev session (npm run dev) pointed at a
// REMOTE Supabase project with no table prefix, reads/writes hit the real PRODUCTION
// tables — almost never intended locally and a silent way to pollute the live
// leaderboard. Warn loudly. A production build legitimately uses "" (and is not DEV).
const SUPABASE_IS_REMOTE = !/^https?:\/\/(localhost|127\.0\.0\.1)\b/i.test(
  SUPABASE_URL
);
if (import.meta.env.DEV && SUPABASE_IS_REMOTE && TABLE_PREFIX === "") {
  console.warn(
    "[leaderboard] VITE_TABLE_PREFIX is not set while pointed at a remote Supabase " +
      "project — reads/writes will hit the PRODUCTION tables. Set VITE_TABLE_PREFIX=dev_ " +
      "in .env.local to use the shared dev tables."
  );
}

// --- Initialization ---
let supabase = null;
try {
  // persistSession + detectSessionInUrl are on by default, but we depend on both
  // (the magic-link flow lands tokens in the URL hash and the editor reads the
  // session back after a full page reload) — make them explicit.
  supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  console.log("Supabase client initialized.");
} catch (error) {
  console.error("Error initializing Supabase client:", error);
  // Handle initialization error appropriately in your UI
}

// --- State ---
let currentChallenge = null;
let currentSignedChallenge = null;

// --- Constants ---
const CLIENT_VERSION = "1.0.0"; // Example client version

// --- Helper Functions ---

/**
 * Calculates the SHA-256 hash of a string using the browser's SubtleCrypto API.
 * @param {string} message - The string to hash.
 * @returns {Promise<string>} The SHA-256 hash as a hex string.
 */
async function calculateSHA256(message) {
  const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(""); // convert bytes to hex string
  return hashHex;
}

/**
 * Constructs the string used for checksum calculation.
 * IMPORTANT: Must EXACTLY match the server-side implementation in the submit-score function.
 * @param {number} score
 * @param {number} durationMs
 * @param {string | null | undefined} playerName
 * @param {string | null | undefined} clientVersion
 * @param {string} challenge - The raw challenge string.
 * @param {string | null | undefined} mapId - The active map id (per-map boards).
 * @returns {string}
 */
function constructChecksumString(
  score,
  durationMs,
  playerName,
  clientVersion,
  challenge,
  mapId
) {
  return `${score}|${durationMs}|${playerName || ""}|${
    clientVersion || ""
  }|${challenge}|${mapId || ""}`;
}

// --- Service Functions ---

/**
 * Fetches a new challenge from the Supabase function.
 * Stores the challenge and signed challenge for later use.
 */
async function fetchChallenge() {
  if (!supabase) {
    console.error("Supabase client not initialized. Cannot fetch challenge.");
    return;
  }
  // Don't fetch if a valid challenge already exists and is relatively fresh
  // (Could add timestamp checking here if desired, but for now just check existence)
  // if (currentChallenge && currentSignedChallenge) {
  //     console.log("Valid challenge already exists.");
  //     return;
  // }

  console.log("Fetching new challenge...");
  try {
    // Ensure function name matches exactly what `supabase functions deploy` uses
    const { data, error, status } = await supabase.functions.invoke(
      "get-challenge",
      {
        method: "POST",
      }
    );

    if (error) {
      console.error(`Function invoke error (Status ${status}):`, error.message);
      throw error; // Let the outer catch handle it
    }

    if (status >= 400) {
      console.error(`Function returned error status: ${status}`, data);
      throw new Error(`Server error ${status}`);
    }

    if (data && data.challenge && data.signedChallenge) {
      currentChallenge = data.challenge;
      currentSignedChallenge = data.signedChallenge;
      console.log("Challenge received successfully.");
      // console.log("Raw Challenge:", currentChallenge); // For debugging
      // console.log("Signed Challenge:", currentSignedChallenge); // For debugging
    } else {
      console.error("Invalid response format from get-challenge:", data);
      currentChallenge = null;
      currentSignedChallenge = null;
    }
  } catch (error) {
    console.error("Error fetching challenge:", error.message || error);
    currentChallenge = null;
    currentSignedChallenge = null;
    // Implement user feedback if necessary, maybe retry after a delay?
  }
}

/**
 * Submits the game score along with validation data to the Supabase function.
 * @param {number} score - Final score.
 * @param {number} gameDurationMs - Duration of the game in milliseconds.
 * @param {string | null} playerName - Player's name (optional).
 * @param {string | null} mapId - The active map id, for per-map leaderboards (optional).
 * @param {object | null} runStats - Descriptive end-of-run statistics (optional).
 *   Stored alongside the score but NOT part of the anti-cheat checksum (they don't
 *   affect ranking); the server validates/bounds them. Shape:
 *   { outcome: "died"|"finished", deathCause: string|null, mapSegment: string|null,
 *     previousMapSegment: string|null, endPositionX: number|null,
 *     endPositionY: number|null, distanceM: number|null }.
 * @returns {Promise<{success: boolean, message: string | null, scoreId?: number, challengeJwt?: string}>}
 */
async function submitScore(
  score,
  gameDurationMs,
  playerName = null,
  mapId = null,
  runStats = null
) {
  if (!supabase) {
    console.error("Supabase client not initialized. Cannot submit score.");
    return { success: false, message: "Supabase not initialized." };
  }
  if (!currentChallenge || !currentSignedChallenge) {
    console.error("No valid challenge available. Cannot submit score.");
    // Attempt to fetch a challenge synchronously before failing? Risky.
    // Best practice is to ensure a challenge is fetched *before* it's needed.
    return { success: false, message: "Missing challenge token." };
  }

  console.log(
    `Submitting score: ${score}, Duration: ${gameDurationMs}ms, Player: ${playerName}`
  );

  try {
    // 1. Construct the checksum string using the RAW challenge
    const checksumString = constructChecksumString(
      score,
      gameDurationMs,
      playerName,
      CLIENT_VERSION,
      currentChallenge, // Use the raw challenge here
      mapId
    );

    // 2. Calculate the checksum hash
    const clientChecksum = await calculateSHA256(checksumString);
    // console.log("Calculated Client Checksum:", clientChecksum); // For debugging

    // 3. Prepare the payload for the submit-score function
    const payload = {
      playerName: playerName,
      score: score,
      gameDurationMs: gameDurationMs,
      clientChecksum: clientChecksum,
      clientVersion: CLIENT_VERSION,
      signedChallenge: currentSignedChallenge, // Pass JWT in body
      mapId: mapId, // Which map this score is from (per-map leaderboards)
      tablePrefix: TABLE_PREFIX, // Route to dev_runs vs runs (server-validated)
      // Descriptive end-of-run stats (server-validated/bounded; not checksummed).
      // Sent as null when absent so older payloads stay valid.
      outcome: runStats?.outcome ?? null,
      deathCause: runStats?.deathCause ?? null,
      mapSegment: runStats?.mapSegment ?? null,
      previousMapSegment: runStats?.previousMapSegment ?? null,
      endPositionX: runStats?.endPositionX ?? null,
      endPositionY: runStats?.endPositionY ?? null,
      distanceM: runStats?.distanceM ?? null,
    };

    // Clear the used challenge immediately before the attempt
    // const challengeToSubmit = currentChallenge; // Not needed directly
    const signedChallengeToClear = currentSignedChallenge; // Keep track of which to clear
    currentChallenge = null;
    currentSignedChallenge = null;
    // Proactively fetch the next challenge in the background
    fetchChallenge();

    // 4. Invoke the function using supabase.functions.invoke
    try {
      const { data, error, status } = await supabase.functions.invoke(
        "submit-score",
        {
          method: "POST",
          // NO Authorization header
          body: payload, // Send payload with signedChallenge included
        }
      );

      if (error) {
        // Supabase client throws for function errors (like 4xx, 5xx responses)
        console.error(
          `Error submitting score (Status ${status}):`,
          error.message || error
        );

        let errorMessage = "Failed to submit score.";
        try {
          // Deno functions often return { error: "message" } in the body on failure
          const functionError = JSON.parse(error.context?.responseText || "{}");
          if (functionError.error) {
            errorMessage = `Submit failed: ${functionError.error}`;
          } else if (data?.error) {
            // Check data object too
            errorMessage = `Submit failed: ${data.error}`;
          }
        } catch (parseError) {
          /* Ignore if response is not JSON */
        }

        return { success: false, message: errorMessage };
      }

      // Note: Supabase client might handle non-2xx status codes differently than fetch.
      // If the function returns { error: ... } with a 2xx status, it might not land in the 'error' block above.
      if (status >= 400 || (data && data.error && !data.success)) {
        console.error(
          `Function returned error status ${status} or error in data:`,
          data
        );
        return {
          success: false,
          message: data?.error || `Server error ${status}`,
        };
      }

      console.log("Score submitted successfully:", data);
      // Use the JWT we *sent* for the return value, as the server might not return it anymore
      return {
        success: true,
        message: data?.message || "Score submitted.",
        scoreId: data?.scoreId,
        challengeJwt: signedChallengeToClear, // Return the JWT we sent
      };
    } catch (invokeError) {
      // Catch errors specifically from the invoke process itself (e.g., network issues)
      console.error("Error invoking submit-score function:", invokeError);
      return { success: false, message: "Network error invoking function." };
    }
  } catch (error) {
    // Catch any unexpected errors during checksum calculation or processing
    console.error("Unexpected error during score submission:", error);
    return { success: false, message: "An unexpected error occurred." };
  }
}

/**
 * Fetches the top N scores from the leaderboard.
 * @param {number} limit - Number of scores to fetch.
 * @param {string | null} mapId - If provided, only scores from this map (per-map
 *   board). Omit/null for the global board across all maps (back-compat).
 * @returns {Promise<Array<object>|null>} - Array of score objects or null on error.
 */
async function getLeaderboard(limit = 10, mapId = null) {
  if (!supabase) {
    console.error("Supabase client not initialized. Cannot fetch leaderboard.");
    return null;
  }
  console.log(`Fetching leaderboard${mapId ? ` for map "${mapId}"` : ""}...`);
  try {
    let query = supabase
      .from(`${TABLE_PREFIX}runs`)
      .select("player_name, score, game_duration_ms, submitted_at") // Select desired columns
      .eq("is_validated", true); // Only fetch validated runs

    // Scope to a single map when requested.
    if (mapId) {
      query = query.eq("map_id", mapId);
    }

    const { data, error } = await query
      .order("score", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Supabase DB Error:", error);
      throw error;
    }
    console.log("Leaderboard data received:", data);
    return data;
  } catch (error) {
    console.error("Error fetching leaderboard:", error.message || error);
    return null;
  }
}

/**
 * Updates the player name for a specific score record on the server.
 * @param {string | number} scoreId - The ID of the score record to update.
 * @param {string} newName - The new player name.
 * @param {string} challengeJwt - The JWT obtained during the initial score submission.
 * @returns {Promise<{success: boolean, message: string | null}>}
 */
async function updatePlayerName(scoreId, newName, challengeJwt) {
  if (!supabase) {
    console.error(
      "Supabase client not initialized. Cannot update player name."
    );
    return { success: false, message: "Supabase not initialized." };
  }
  if (!scoreId || !newName || !challengeJwt) {
    console.error("Missing required arguments for updatePlayerName.");
    return {
      success: false,
      message: "Missing scoreId, newName, or challengeJwt.",
    };
  }

  console.log(`Updating player name for score ${scoreId} to ${newName}...`);

  // Invoke using supabase.functions.invoke
  try {
    // Prepare body with JWT included
    const payload = {
      scoreId: scoreId,
      newName: newName,
      signedChallenge: challengeJwt, // Pass JWT in body
      tablePrefix: TABLE_PREFIX, // Route to dev_runs vs runs (server-validated)
    };

    const { data, error, status } = await supabase.functions.invoke(
      "update-player-name",
      {
        method: "POST",
        // NO Authorization header
        body: payload,
      }
    );

    if (error) {
      // Handle errors similar to submitScore
      console.error(
        `Error updating player name (Status ${status}):`,
        error.message || error
      );
      let errorMessage = "Failed to update player name.";
      try {
        const functionError = JSON.parse(error.context?.responseText || "{}");
        if (functionError.error) {
          errorMessage = `Update failed: ${functionError.error}`;
        } else if (data?.error) {
          errorMessage = `Update failed: ${data.error}`;
        }
      } catch (parseError) {
        /* Ignore */
      }
      return { success: false, message: errorMessage };
    }

    if (status >= 400 || (data && data.error && !data.success)) {
      console.error(
        `Function returned error status ${status} or error in data:`,
        data
      );
      return {
        success: false,
        message: data?.error || `Server error ${status}`,
      };
    }

    console.log("Player name updated successfully:", data);
    return { success: true, message: data?.message || "Name updated." };
  } catch (invokeError) {
    console.error("Error invoking update-player-name function:", invokeError);
    return { success: false, message: "Network error invoking function." };
  }
}

// --- Auth + cloud level backup -----------------------------------------------
//
// Unlike the leaderboard (anonymous, validated via Edge Functions), player-owned
// levels use real Supabase Auth: the anon client carries the signed-in user's
// JWT, and RLS on the profiles/levels tables (see the create_user_levels
// migration) scopes every read/write to auth.uid(). So these talk to the tables
// directly — no Edge Function needed. The raw client stays private; callers use
// only the named functions below. All level/profile tables share the dev_/""
// prefix the leaderboard uses.

const PROFILES_TABLE = `${TABLE_PREFIX}profiles`;
const LEVELS_TABLE = `${TABLE_PREFIX}levels`;
const PUBLISHED_LEVELS_TABLE = `${TABLE_PREFIX}published_levels`;
const PUBLISHED_VERSIONS_TABLE = `${TABLE_PREFIX}published_versions`;
const LEVEL_REVIEWS_TABLE = `${TABLE_PREFIX}level_reviews`;

// Auth uses email + PASSWORD (not magic links), because the only flow that needs
// a redirect — password reset — is the only one that can't run inside an itch.io
// embed. itch serves the game from a CDN host (e.g. html-classic.itch.zone) in a
// cross-origin iframe; a magic/confirmation link opens a NEW top-level tab there,
// so the session never reaches the running embed. signUp / signInWithPassword are
// plain API calls (no redirect), so they work everywhere the game is hosted.

// The origin that can COMPLETE a redirect-based auth flow (password reset). itch
// embeds can't, so reset links always land on the canonical web app; local dev
// stays on localhost so you can test there.
function authRedirectOrigin() {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    const base = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "/");
    return `${window.location.origin}${base}`;
  }
  return "https://marathon.omiigames.com/";
}

/**
 * Create an account with email + password. With email confirmation DISABLED
 * (Supabase Auth settings) a session is returned immediately and SIGNED_IN fires;
 * if confirmation is on, no session comes back and `needsConfirmation` is true.
 * @returns {Promise<{ error: string | null, needsConfirmation?: boolean }>}
 */
async function signUp(email, password) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!email || !password) return { error: "Enter an email and password." };
  try {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error: error.message || "Could not create account." };
    return { error: null, needsConfirmation: !data?.session };
  } catch (err) {
    console.error("signUp failed:", err);
    return { error: err?.message || "Could not create account." };
  }
}

/**
 * Sign in with email + password. No redirect — works inside the itch embed.
 * @returns {Promise<{ error: string | null }>}
 */
async function signInWithPassword(email, password) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!email || !password) return { error: "Enter an email and password." };
  try {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message || "Could not sign in." };
    return { error: null };
  } catch (err) {
    console.error("signInWithPassword failed:", err);
    return { error: err?.message || "Could not sign in." };
  }
}

/**
 * Email a password-reset link. The link lands on the canonical web app's editor
 * hub (see authRedirectOrigin); supabase-js detects the recovery token there and
 * fires PASSWORD_RECOVERY so the page can prompt for a new password.
 * @returns {Promise<{ error: string | null }>}
 */
async function requestPasswordReset(email) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!email) return { error: "Enter your email address." };
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${authRedirectOrigin()}?edit=`,
    });
    if (error) return { error: error.message || "Could not send reset email." };
    return { error: null };
  } catch (err) {
    console.error("requestPasswordReset failed:", err);
    return { error: err?.message || "Could not send reset email." };
  }
}

/**
 * Set a new password for the signed-in (or in-recovery) user.
 * @returns {Promise<{ error: string | null }>}
 */
async function updatePassword(newPassword) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!newPassword || newPassword.length < 6) {
    return { error: "Password must be at least 6 characters." };
  }
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message || "Could not update password." };
    return { error: null };
  } catch (err) {
    console.error("updatePassword failed:", err);
    return { error: err?.message || "Could not update password." };
  }
}

/** Sign the current user out (clears the persisted session). */
async function signOut() {
  if (!supabase) return { error: "Supabase not initialized." };
  try {
    const { error } = await supabase.auth.signOut();
    return { error: error ? error.message : null };
  } catch (err) {
    console.error("signOut failed:", err);
    return { error: err?.message || "Could not sign out." };
  }
}

/** The active session, or null. */
async function getSession() {
  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session || null;
  } catch (err) {
    console.error("getSession failed:", err);
    return null;
  }
}

/** The signed-in user, or null. */
async function getCurrentUser() {
  const session = await getSession();
  return session?.user || null;
}

/**
 * Subscribe to auth-state transitions (SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED).
 * @param {(event: string, session: object | null) => void} cb
 * @returns {() => void} unsubscribe
 */
function onAuthChange(cb) {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    cb(event, session);
  });
  return () => data?.subscription?.unsubscribe?.();
}

// --- Profile / username ---

/** This user's chosen username, or null if they haven't set one. */
async function fetchUsername(userId) {
  if (!supabase || !userId) return null;
  try {
    const { data, error } = await supabase
      .from(PROFILES_TABLE)
      .select("username")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      console.error("fetchUsername failed:", error);
      return null;
    }
    return data?.username || null;
  } catch (err) {
    console.error("fetchUsername threw:", err);
    return null;
  }
}

/**
 * Is `candidate` free? Case-insensitive. Best-effort: a race is still caught by
 * the unique index at write time (see setUsername).
 */
async function isUsernameAvailable(candidate) {
  if (!supabase || !candidate) return false;
  try {
    const { data, error } = await supabase
      .from(PROFILES_TABLE)
      .select("user_id")
      .ilike("username", candidate)
      .limit(1);
    if (error) {
      console.error("isUsernameAvailable failed:", error);
      return false;
    }
    return !data || data.length === 0;
  } catch (err) {
    console.error("isUsernameAvailable threw:", err);
    return false;
  }
}

/**
 * Claim/update this user's username (owner upsert). Maps the unique-index
 * violation (Postgres 23505) to a friendly `taken` error so the UI can re-prompt.
 * @returns {Promise<{ error: string | null }>}
 */
async function setUsername(userId, username) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!userId) return { error: "Not signed in." };
  if (!/^[a-zA-Z0-9_]{3,20}$/.test(username || "")) {
    return { error: "3–20 letters, numbers, or underscores." };
  }
  try {
    const { error } = await supabase
      .from(PROFILES_TABLE)
      .upsert(
        { user_id: userId, username, updated_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    if (error) {
      if (error.code === "23505") return { error: "That username is taken." };
      console.error("setUsername failed:", error);
      return { error: error.message || "Could not save username." };
    }
    return { error: null };
  } catch (err) {
    console.error("setUsername threw:", err);
    return { error: err?.message || "Could not save username." };
  }
}

// --- Cloud levels (RLS scopes every row to the signed-in user) ---

/**
 * All of this user's cloud levels, mapped to the editor's StoredMap shape.
 * @returns {Promise<Array<{id,name,description,updatedAt,widthScenes,rows}> | null>}
 *   null on error (caller should keep local state untouched).
 */
async function fetchCloudLevels() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from(LEVELS_TABLE)
      .select("client_id, name, description, width_scenes, rows, updated_at, slug");
    if (error) {
      console.error("fetchCloudLevels failed:", error);
      return null;
    }
    return (data || []).map((row) => ({
      id: row.client_id,
      name: row.name || "Untitled Level",
      description: row.description || "",
      updatedAt: row.updated_at ? Date.parse(row.updated_at) : 0,
      widthScenes: Math.max(1, row.width_scenes || 1),
      rows: Array.isArray(row.rows) ? row.rows : [],
      // Carry the public share slug through so it survives sign-in hydrate/merge —
      // without this, shared links would break every time levels re-sync.
      slug: row.slug || null,
    }));
  } catch (err) {
    console.error("fetchCloudLevels threw:", err);
    return null;
  }
}

/**
 * Insert or update one level for the signed-in user (keyed by client_id). When the
 * StoredMap carries a `slug`, it's persisted too; a per-user slug collision (the
 * (user_id, slug) unique index → Postgres 23505) is surfaced as `slugTaken: true`
 * so the sync layer can regenerate an auto slug.
 * @param {object} stored a StoredMap ({ id, name, description, widthScenes, rows, updatedAt, slug? })
 * @returns {Promise<{ error: string | null, slugTaken?: boolean }>}
 */
async function upsertCloudLevel(stored) {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Not signed in." };
  try {
    const row = {
      user_id: user.id,
      client_id: stored.id,
      name: stored.name || "Untitled Level",
      description: stored.description || "",
      width_scenes: Math.max(1, stored.widthScenes || 1),
      rows: Array.isArray(stored.rows) ? stored.rows : [],
      updated_at: new Date(stored.updatedAt || Date.now()).toISOString(),
    };
    // Only send slug when set, so a null doesn't clobber an existing cloud slug.
    if (stored.slug) row.slug = stored.slug;
    const { error } = await supabase
      .from(LEVELS_TABLE)
      .upsert(row, { onConflict: "user_id,client_id" });
    if (error) {
      if (error.code === "23505") return { error: "slug taken", slugTaken: true };
      console.error("upsertCloudLevel failed:", error);
      return { error: error.message || "Could not save to cloud." };
    }
    return { error: null };
  } catch (err) {
    console.error("upsertCloudLevel threw:", err);
    return { error: err?.message || "Could not save to cloud." };
  }
}

/**
 * Is `slug` free for the signed-in user? Slugs are unique PER user, so this is
 * scoped to the current user's own rows and excludes `exceptClientId` (the level
 * being renamed, so setting a slug to its current value isn't "taken"). Best-effort
 * — the unique index is the race backstop (see updateLevelSlug).
 */
async function isSlugAvailable(slug, exceptClientId = null) {
  if (!supabase || !slug) return false;
  const user = await getCurrentUser();
  if (!user) return false;
  try {
    let query = supabase
      .from(LEVELS_TABLE)
      .select("client_id")
      .eq("user_id", user.id)
      .eq("slug", slug);
    if (exceptClientId) query = query.neq("client_id", exceptClientId);
    const { data, error } = await query.limit(1);
    if (error) {
      console.error("isSlugAvailable failed:", error);
      return false;
    }
    return !data || data.length === 0;
  } catch (err) {
    console.error("isSlugAvailable threw:", err);
    return false;
  }
}

/**
 * Set the public slug on one of the user's levels (by client_id). Maps the
 * (user_id, slug) unique-index violation (23505) to a friendly `taken` error.
 * @returns {Promise<{ error: string | null }>}
 */
async function updateLevelSlug(clientId, slug) {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Not signed in." };
  try {
    const { error } = await supabase
      .from(LEVELS_TABLE)
      .update({ slug })
      .eq("user_id", user.id)
      .eq("client_id", clientId);
    if (error) {
      if (error.code === "23505") return { error: "That id is taken." };
      console.error("updateLevelSlug failed:", error);
      return { error: error.message || "Could not update the id." };
    }
    return { error: null };
  } catch (err) {
    console.error("updateLevelSlug threw:", err);
    return { error: err?.message || "Could not update the id." };
  }
}

/**
 * Fetch a community (someone else's) level by "<username>/<slug>" via the public
 * SECURITY DEFINER RPC. Returns the level content in StoredMap shape (no id —
 * callers synthesize the runtime id), or null if not found / on error.
 * @returns {Promise<{ name, description, widthScenes, rows } | null>}
 */
async function getPublicLevel(username, slug) {
  if (!supabase || !username || !slug) return null;
  try {
    const { data, error } = await supabase.rpc(`${TABLE_PREFIX}get_public_level`, {
      p_username: username,
      p_slug: slug,
    });
    if (error) {
      console.error("getPublicLevel failed:", error);
      return null;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      name: row.name || "Untitled Level",
      description: row.description || "",
      widthScenes: Math.max(1, row.width_scenes || 1),
      rows: Array.isArray(row.rows) ? row.rows : [],
    };
  } catch (err) {
    console.error("getPublicLevel threw:", err);
    return null;
  }
}

/** Delete one of this user's cloud levels by its client_id. */
async function deleteCloudLevel(clientId) {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Not signed in." };
  try {
    const { error } = await supabase
      .from(LEVELS_TABLE)
      .delete()
      .eq("user_id", user.id)
      .eq("client_id", clientId);
    if (error) {
      console.error("deleteCloudLevel failed:", error);
      return { error: error.message || "Could not delete from cloud." };
    }
    return { error: null };
  } catch (err) {
    console.error("deleteCloudLevel threw:", err);
    return { error: err?.message || "Could not delete from cloud." };
  }
}

// --- Publishing & versioning (RLS-public reads; writes via SECURITY DEFINER RPCs) ---

/**
 * Publish a new immutable version of the signed-in user's draft level. Snapshots
 * the draft (by client_id) into a published_versions row and upserts the public
 * catalog entry — re-publishing the same draft adds a version to the same entry.
 *
 * The public address is `<username>/<slug>`, so this requires (a) being signed in,
 * (b) a username, and (c) the draft already having a share slug. Those are returned
 * as `needsSignIn` / `needsUsername` / `needsSlug` flags so the UI can show the right
 * call to action. The slug is the DRAFT's slug (not a parameter) — set on the first
 * publish and locked thereafter; the score-rate cap is derived server-side.
 * @param {string} clientId the draft's local id ("custom-…")
 * @param {{title?:string, description?:string, changelog?:string}} opts
 * @returns {Promise<{versionId, versionNumber, publishedLevelId, slug, error:null}
 *   | {error:string, needsSignIn?:boolean, needsUsername?:boolean, needsSlug?:boolean}>}
 */
async function publishLevel(clientId, { title, description, changelog } = {}) {
  if (!supabase) return { error: "Supabase not initialized." };
  if (!clientId) return { error: "Missing level id." };
  const user = await getCurrentUser();
  if (!user) return { error: "Sign in to publish your level.", needsSignIn: true };
  const username = await fetchUsername(user.id);
  if (!username) {
    return { error: "Choose a username before publishing.", needsUsername: true };
  }
  // The published address is the draft's share slug (set on first publish, then
  // locked). Validate it up front for a clear message; the server is authoritative.
  const stored = getCustomMap(clientId);
  if (!stored || !isValidSlug(stored.slug)) {
    return {
      error: "Give this level a share link before publishing.",
      needsSlug: true,
    };
  }
  try {
    const { data, error } = await supabase.rpc(
      `${TABLE_PREFIX}publish_level_version`,
      {
        p_client_id: clientId,
        p_title: title || stored.name || "Untitled Level",
        p_description: description ?? stored.description ?? "",
        p_changelog: changelog || "",
      }
    );
    if (error) {
      // The draft's slug collides with another of this user's published levels.
      if (error.code === "23505") {
        return {
          error: "That share link is already used by another published level.",
        };
      }
      console.error("publishLevel failed:", error);
      return { error: error.message || "Could not publish." };
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      // Server gate returned empty despite the pre-checks above — a race, or the
      // draft hasn't synced to the cloud yet (publish reads the cloud copy).
      return {
        error: "Could not publish — make sure the level is saved to your account.",
      };
    }
    return {
      publishedLevelId: row.published_level_id,
      versionId: row.version_id,
      versionNumber: row.version_number,
      slug: row.slug,
      error: null,
    };
  } catch (err) {
    console.error("publishLevel threw:", err);
    return { error: err?.message || "Could not publish." };
  }
}

/**
 * Fetch a published level for play, by "<username>/<slug>". Returns the current
 * live version unless `version` (a version_number) is given (old versions stay
 * playable). The returned `versionId` is the leaderboard map id to play/score by.
 * @returns {Promise<{versionId, versionNumber, name, description, widthScenes,
 *   rows, publishedLevelId, ratingAvg, ratingCount} | null>}
 */
async function getPublishedLevel(username, slug, version = null) {
  if (!supabase || !username || !slug) return null;
  try {
    const { data, error } = await supabase.rpc(
      `${TABLE_PREFIX}get_published_level`,
      { p_username: username, p_slug: slug, p_version: version }
    );
    if (error) {
      console.error("getPublishedLevel failed:", error);
      return null;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      versionId: row.version_id,
      versionNumber: row.version_number,
      name: row.name || "Untitled Level",
      description: row.description || "",
      widthScenes: Math.max(1, row.width_scenes || 1),
      rows: Array.isArray(row.rows) ? row.rows : [],
      publishedLevelId: row.published_level_id,
      ratingAvg: row.rating_avg != null ? Number(row.rating_avg) : 0,
      ratingCount: row.rating_count || 0,
    };
  } catch (err) {
    console.error("getPublishedLevel threw:", err);
    return null;
  }
}

/**
 * Browse the public catalog of listed levels. `sort` is one of "recent"
 * (last_published_at), "top_rated" (rating_avg), "popular" (play_count).
 * @returns {Promise<Array<object> | null>} null on error.
 */
async function fetchPublishedLevels({ sort = "recent", limit = 24 } = {}) {
  if (!supabase) return null;
  const SORT_COLUMN = {
    recent: "last_published_at",
    top_rated: "rating_avg",
    popular: "play_count",
  };
  const column = SORT_COLUMN[sort] || SORT_COLUMN.recent;
  try {
    const { data, error } = await supabase
      .from(PUBLISHED_LEVELS_TABLE)
      .select(
        "id, user_id, slug, title, description, version_count, rating_avg, rating_count, play_count, last_published_at"
      )
      .eq("is_listed", true)
      .order(column, { ascending: false })
      .limit(limit);
    if (error) {
      console.error("fetchPublishedLevels failed:", error);
      return null;
    }
    return data || [];
  } catch (err) {
    console.error("fetchPublishedLevels threw:", err);
    return null;
  }
}

/**
 * This user's own published levels (RLS includes their unlisted ones), for a
 * creator dashboard. @returns {Promise<Array<object> | null>}
 */
async function fetchMyPublishedLevels() {
  if (!supabase) return null;
  const user = await getCurrentUser();
  if (!user) return [];
  try {
    const { data, error } = await supabase
      .from(PUBLISHED_LEVELS_TABLE)
      .select(
        "id, source_client_id, slug, title, description, version_count, current_version_id, rating_avg, rating_count, play_count, is_listed, last_published_at"
      )
      .eq("user_id", user.id)
      .order("last_published_at", { ascending: false });
    if (error) {
      console.error("fetchMyPublishedLevels failed:", error);
      return null;
    }
    return data || [];
  } catch (err) {
    console.error("fetchMyPublishedLevels threw:", err);
    return null;
  }
}

/**
 * Version history for a published level (newest first) — for the owner's version
 * list and per-version leaderboard switching. @returns {Promise<Array|null>}
 */
async function fetchPublishedVersions(publishedLevelId) {
  if (!supabase || !publishedLevelId) return null;
  try {
    const { data, error } = await supabase
      .from(PUBLISHED_VERSIONS_TABLE)
      .select("id, version_number, name, changelog, max_score_rate_per_ms, published_at")
      .eq("published_level_id", publishedLevelId)
      .order("version_number", { ascending: false });
    if (error) {
      console.error("fetchPublishedVersions failed:", error);
      return null;
    }
    return data || [];
  } catch (err) {
    console.error("fetchPublishedVersions threw:", err);
    return null;
  }
}

/** Owner toggle: list/unlist a published level (hides it from browse only). */
async function setPublishedLevelListed(publishedLevelId, listed) {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Not signed in." };
  try {
    const { error } = await supabase.rpc(
      `${TABLE_PREFIX}set_published_level_listed`,
      { p_published_level_id: publishedLevelId, p_listed: !!listed }
    );
    if (error) {
      console.error("setPublishedLevelListed failed:", error);
      return { error: error.message || "Could not update visibility." };
    }
    return { error: null };
  } catch (err) {
    console.error("setPublishedLevelListed threw:", err);
    return { error: err?.message || "Could not update visibility." };
  }
}

/**
 * Aggregated run metrics for a published level (across its versions) — completion
 * rate, death causes, per-scene death counts. Built on the runs stat columns.
 * @returns {Promise<Array<object> | null>}
 */
async function fetchLevelMetrics(publishedLevelId) {
  if (!supabase || !publishedLevelId) return null;
  try {
    const { data, error } = await supabase.rpc(
      `${TABLE_PREFIX}get_level_metrics`,
      { p_published_level_id: publishedLevelId }
    );
    if (error) {
      console.error("fetchLevelMetrics failed:", error);
      return null;
    }
    return data || [];
  } catch (err) {
    console.error("fetchLevelMetrics threw:", err);
    return null;
  }
}

// --- Reviews (read public; write requires sign-in, enforced by RLS) ---

/** Reviews for a level (with author usernames), newest first. */
async function fetchReviews(publishedLevelId, limit = 50) {
  if (!supabase || !publishedLevelId) return null;
  try {
    const { data, error } = await supabase.rpc(
      `${TABLE_PREFIX}get_level_reviews`,
      { p_published_level_id: publishedLevelId, p_limit: limit }
    );
    if (error) {
      console.error("fetchReviews failed:", error);
      return null;
    }
    return data || [];
  } catch (err) {
    console.error("fetchReviews threw:", err);
    return null;
  }
}

/** The signed-in user's own review of a level (to prefill the form), or null. */
async function getMyReview(publishedLevelId) {
  if (!supabase || !publishedLevelId) return null;
  const user = await getCurrentUser();
  if (!user) return null;
  try {
    const { data, error } = await supabase
      .from(LEVEL_REVIEWS_TABLE)
      .select("rating, body, updated_at")
      .eq("published_level_id", publishedLevelId)
      .eq("user_id", user.id)
      .maybeSingle();
    if (error) {
      console.error("getMyReview failed:", error);
      return null;
    }
    return data || null;
  } catch (err) {
    console.error("getMyReview threw:", err);
    return null;
  }
}

/**
 * Create or update the signed-in user's review of a level (one per user per level).
 * RLS rejects anonymous callers and reviews for non-existent levels.
 */
async function upsertReview(publishedLevelId, rating, body = "") {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Sign in to leave a review." };
  const stars = Math.round(Number(rating));
  if (!Number.isInteger(stars) || stars < 1 || stars > 5) {
    return { error: "Rating must be 1–5 stars." };
  }
  try {
    const { error } = await supabase.from(LEVEL_REVIEWS_TABLE).upsert(
      {
        published_level_id: publishedLevelId,
        user_id: user.id,
        rating: stars,
        body: (body || "").slice(0, 2000),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "published_level_id,user_id" }
    );
    if (error) {
      console.error("upsertReview failed:", error);
      return { error: error.message || "Could not save your review." };
    }
    return { error: null };
  } catch (err) {
    console.error("upsertReview threw:", err);
    return { error: err?.message || "Could not save your review." };
  }
}

/** Delete the signed-in user's own review of a level. */
async function deleteReview(publishedLevelId) {
  if (!supabase) return { error: "Supabase not initialized." };
  const user = await getCurrentUser();
  if (!user) return { error: "Not signed in." };
  try {
    const { error } = await supabase
      .from(LEVEL_REVIEWS_TABLE)
      .delete()
      .eq("published_level_id", publishedLevelId)
      .eq("user_id", user.id);
    if (error) {
      console.error("deleteReview failed:", error);
      return { error: error.message || "Could not delete your review." };
    }
    return { error: null };
  } catch (err) {
    console.error("deleteReview threw:", err);
    return { error: err?.message || "Could not delete your review." };
  }
}

// --- Exports ---
export {
  // supabase, // Avoid exporting the raw client if possible
  fetchChallenge,
  submitScore,
  getLeaderboard,
  updatePlayerName,
  // Auth
  signUp,
  signInWithPassword,
  requestPasswordReset,
  updatePassword,
  signOut,
  getSession,
  getCurrentUser,
  onAuthChange,
  // Profile / username
  fetchUsername,
  isUsernameAvailable,
  setUsername,
  // Cloud levels
  fetchCloudLevels,
  upsertCloudLevel,
  deleteCloudLevel,
  // Sharing
  isSlugAvailable,
  updateLevelSlug,
  getPublicLevel,
  // Publishing & versioning
  publishLevel,
  getPublishedLevel,
  fetchPublishedLevels,
  fetchMyPublishedLevels,
  fetchPublishedVersions,
  setPublishedLevelListed,
  fetchLevelMetrics,
  // Reviews
  fetchReviews,
  getMyReview,
  upsertReview,
  deleteReview,
};

// --- Initial Setup ---
// Fetch the first challenge when the module loads.
// This ensures a challenge is ready when the game starts quickly.
fetchChallenge();
